/**
 * Trademarks service — live from W3.M7.
 *
 * The product says "trademark"; the API says **brand**. That is not drift: a
 * `brand` is a mark *our client owns and we protect*, while a `mark` is a row
 * in a published bulletin that might conflict with one. Two different tables
 * for two different things. This file is the translation layer, and it is the
 * only place in the SPA that needs to know.
 *
 * Live contract, as implemented in `@backend`:
 *
 *   GET    /brands?q=&client_id=&mark_type=&country=&feed=&nice_class=
 *                 &monitored=&sort=&limit=&offset=      → Page<BrandOut>
 *   GET    /brands/summary                              → BrandSummary
 *   GET    /brands/:id                                  → BrandOut
 *   POST   /brands   { client_id, title, country, … }   → BrandOut
 *   PATCH  /brands/:id                                  → BrandOut
 *   DELETE /brands/:id                                  → 204
 *
 * The old contract in this file (`/trademarks`, camelCase) never existed on any
 * server — flipping the mock flag would have produced a page of 404s.
 */
import { apiResolve, apiEcho, http, dedupe } from '@/api'
import { isLive } from '@/api/config'
import i18n from '@/i18n'
import { createTrademark, createTrademarkList } from '@/entities/trademark'
import type { Trademark } from '@/entities/trademark'
import portfolio from '@/mock/portfolio.json'
import type { Filters, ServiceOptions } from './types'
import type { SortState } from '@/stores/types'
import type { Paged } from './clients.service'

const LIVE = () => isLive('brands')

/* ---------------------------------------------------------------- words
 *
 * The labels a trademark is described in, read from the catalogue (`tm.*`)
 * the way `utils/caseText.ts` does, so the list, the row and the dossier say
 * the same thing in the reader's language. Called during render, so a
 * language switch re-labels them: `i18n.global.t` reads the locale ref. */
const t = (key: string, named?: Record<string, unknown>): string => {
  const g = i18n.global as unknown as { t: (k: string, n?: Record<string, unknown>) => string }
  return named ? g.t(key, named) : g.t(key)
}

/** A catalogue lookup that falls back to the raw value for a key we do not know. */
function known(key: string, fallback: string): string {
  const text = t(key)
  return text === key ? fallback : text
}

/**
 * A registry status as a lawyer reads it. An unknown one is printed as sent
 * rather than as "—": a status nobody translated yet is still the mark's
 * legal state, and a dash in the one column a lawyer scans for it says
 * nothing.
 */
export function statusText(status: string | null | undefined): string {
  if (!status) return '—'
  return known(`tm.status.${status.replace(/-/g, '_')}`, status)
}

/** `wordmark` / `figurative` / … in the reader's language. */
export function markTypeText(type: string | null | undefined): string {
  if (!type) return '—'
  return known(`tm.type.${type === 'word' ? 'wordmark' : type}`, type)
}

/** The short monitoring pill: Active / Paused / Not monitored. */
export function monitoringTagText(state: string | null | undefined): string {
  return known(`tm.monitoringTag.${state || 'none'}`, state ?? '')
}

/** The same state as a sentence-length label, for the preview and dossier. */
export function monitoringText(state: string | null | undefined): string {
  return known(`tm.monitoring.${state || 'none'}`, state ?? '')
}

/** What the API sends for one mark. */
export interface BrandOut {
  id: number
  firm_id: number
  client_id: number
  title: string
  description: string | null
  mark_type: string
  country: string
  application_number: string | null
  registration_number: string | null
  application_date: string | null
  registration_date: string | null
  expires_on: string | null
  registry_status: string | null
  logo_uri: string | null
  created_at: string
  updated_at: string
  nice_entries: { class_number: number; texts?: Record<string, string> }[]
  scopes: { feed_id: number; active: boolean; last_scan_at: string | null }[]
  client_name: string | null
  feeds: string[]
  monitored: boolean
  open_match_count: number
  open_case_count: number
}

/** A file attached to a mark — certificate, evidence, correspondence, logo. */
export interface BrandDoc {
  id: number
  brand_id: number
  kind: string
  file_name: string
  content_type: string
  size_bytes: number
  sha256: string
  uploaded_by_user_id: number | null
  created_at: string
}

export type DocumentKind = 'logo' | 'certificate' | 'evidence' | 'correspondence' | 'other'

/** What the API sends for the KPI row. */
interface BrandSummaryOut {
  total: number
  monitored: number
  unmonitored: number
  new_this_month: number
  jurisdictions: number
  open_matches: number
  expiring_90_days: number
  by_feed: Record<string, number>
  by_mark_type: Record<string, number>
  by_country: Record<string, number>
}

/** What the KPI row reads. Named as the UI names things, not as SQL does. */
export interface TrademarkSummary {
  total: number
  monitored: number
  unmonitored: number
  monitoredPct: number
  addedThisMonth: number
  jurisdictions: number
  openMatches: number
  expiringSoon: number
  byFeed: Record<string, number>
  byMarkType: Record<string, number>
  byCountry: Record<string, number>
}

export const EMPTY_SUMMARY: TrademarkSummary = {
  total: 0,
  monitored: 0,
  unmonitored: 0,
  monitoredPct: 0,
  addedThisMonth: 0,
  jurisdictions: 0,
  openMatches: 0,
  expiringSoon: 0,
  byFeed: {},
  byMarkType: {},
  byCountry: {},
}

export function summaryFromApi(row: BrandSummaryOut): TrademarkSummary {
  const total = row.total ?? 0
  return {
    total,
    monitored: row.monitored ?? 0,
    unmonitored: row.unmonitored ?? 0,
    /* Derived here rather than served: it is a rendering of two numbers the
     * API already sends, and a server-side percentage would just be a second
     * thing to keep consistent with them. */
    monitoredPct: total ? Math.round(((row.monitored ?? 0) / total) * 100) : 0,
    addedThisMonth: row.new_this_month ?? 0,
    jurisdictions: row.jurisdictions ?? 0,
    openMatches: row.open_matches ?? 0,
    expiringSoon: row.expiring_90_days ?? 0,
    byFeed: row.by_feed ?? {},
    byMarkType: row.by_mark_type ?? {},
    byCountry: row.by_country ?? {},
  }
}

/**
 * Feed id → code, learned from the catalogue on first use.
 *
 * `BrandOut.scopes` carries feed *ids* while `BrandOut.feeds` carries codes
 * sorted alphabetically — so the two lists cannot be zipped by position for a
 * mark watched on several registries. Populated by `feeds()` below; until then
 * a scope is labelled "Feed 2" (in the reader's language), which is ugly but
 * not wrong — and the dossier relabels it once the catalogue arrives.
 */
const FEED_CODES: Record<number, string> = {}

/** `word` in the database, `wordmark` in the UI's vocabulary and CSS. */
const TYPE_IN: Record<string, string> = {
  word: 'wordmark',
  figurative: 'figurative',
  combined: 'combined',
  sound: 'sound',
  other: 'other',
}
const TYPE_OUT: Record<string, string> = { wordmark: 'word' }

/**
 * How a monitoring *scope selection* becomes the UI's tri-state.
 *
 * The database models this as rows in `brand_scopes`, each with its own
 * `active` flag; the UI has one pill per mark. So: no scope rows at all means
 * nobody chose to watch this mark ('none'), scope rows that are all switched
 * off means watching was deliberately stopped ('paused'), and anything active
 * is 'active'. The distinction matters — "never set up" and "turned off" look
 * the same on a screen that only knows on/off, and they need different actions.
 */
function monitoringOf(row: BrandOut): string {
  if (!row.scopes?.length) return 'none'
  return row.monitored ? 'active' : 'paused'
}

function initialsOf(name: string): string {
  return (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toUpperCase()
}

/**
 * BrandOut → the `Trademark` the pages render.
 *
 * Left at their defaults on purpose, because the schema has no home for them:
 * `risk`/`riskScore` (brands carry no risk score — see the entity's note) and
 * `viennaCodes` (the catalogue exists, but nothing links a brand to one yet).
 * The fixtures supplied all three, which is how the portfolio page came to
 * display a high-risk count that no query could have produced.
 */
export function trademarkFromApi(row: BrandOut): Trademark {
  return createTrademark({
    id: row.id,
    name: row.title,
    initials: initialsOf(row.title),
    /* `logo_uri` is a blob key, not a URL. The bytes come from
     * `GET /brands/:id/logo`; until a component asks for them the row renders
     * initials, which is what the mockup does for a mark with no logo. */
    logo: null,
    /* Whether asking for those bytes is worth it. Without this a component
     * cannot tell "no picture" from "picture not fetched yet", so it either
     * requests one per row — fifty authenticated blob fetches to render a
     * table — or never requests any and every real logo stays invisible. */
    hasLogo: Boolean(row.logo_uri),
    regNumber: row.registration_number ?? row.application_number ?? '',
    regDate: row.registration_date,
    filingDate: row.application_date,
    expiresOn: row.expires_on,
    jurisdiction: row.country,
    classes: (row.nice_entries ?? []).map((e) => String(e.class_number)),
    type: TYPE_IN[row.mark_type] ?? row.mark_type,
    owner: row.client_name ?? '',
    clientId: row.client_id,
    status: row.registry_status ?? 'registered',
    monitoring: monitoringOf(row),
    description: row.description ?? '',
    feeds: row.feeds ?? [],
    /* Zipped with `feeds` by index: the API sorts feed codes alphabetically in
     * `feeds` but returns `scopes` in insertion order, so pairing them by
     * position would mislabel a mark watched on more than one registry. Look
     * the code up by id instead. */
    scopes: (row.scopes ?? []).map((s) => ({
      feedId: s.feed_id,
      feedCode: FEED_CODES[s.feed_id] ?? t('tmd.scopes.feedFallback', { id: s.feed_id }),
      active: s.active,
      lastScanAt: s.last_scan_at,
    })),
    openMatches: row.open_match_count ?? 0,
    openCases: row.open_case_count ?? 0,
  })
}

/** UI filter state → the query string the API understands. */
export function toParams(filters: Filters = {}, sort?: SortState | null): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  if (filters.query) params.q = filters.query
  if (filters.clientId != null) params.client_id = filters.clientId
  if (filters.type) params.mark_type = TYPE_OUT[String(filters.type)] ?? filters.type
  if (filters.country) params.country = filters.country
  /* The UI calls this "jurisdiction" and means a feed code (EUIPO / WIPO / BG),
   * which is the registry a mark is *watched in* — not the country it is
   * registered in. Those are different questions and the API has both. */
  if (filters.jurisdiction) params.feed = filters.jurisdiction
  if (filters.niceClass) params.nice_class = filters.niceClass
  if (filters.monitoring === 'active') params.monitored = true
  if (filters.monitoring === 'none') params.monitored = false
  if (filters.limit != null) params.limit = filters.limit
  if (filters.offset != null) params.offset = filters.offset
  if (sort?.field) params.sort = sort.dir === 'desc' ? `-${sort.field}` : sort.field
  else if (filters.sort) params.sort = filters.sort
  return params
}

function applyMockFilters(rows: any[], { query, jurisdiction, status, risk }: Filters = {}) {
  let out = rows
  if (query) {
    const q = query.toLowerCase()
    out = out.filter((t) => (t.name + ' ' + (t.owner || '')).toLowerCase().includes(q))
  }
  if (jurisdiction) out = out.filter((t) => t.jurisdiction === jurisdiction)
  if (status) out = out.filter((t) => t.status === status)
  if (risk) out = out.filter((t) => t.risk === risk)
  return out
}

function applyMockSort(rows: any[], sort?: SortState | null) {
  if (!sort?.field) return rows
  const dir = sort.dir === 'desc' ? -1 : 1
  const f = sort.field
  return [...rows].sort((a, b) => {
    const av = a[f],
      bv = b[f]
    if (av == null) return 1
    if (bv == null) return -1
    if (av < bv) return -1 * dir
    if (av > bv) return 1 * dir
    return 0
  })
}

export default {
  async list(
    filters: Filters = {},
    { signal, sort }: ServiceOptions = {}
  ): Promise<Paged<Trademark>> {
    if (!LIVE()) {
      const key = `GET /brands ${JSON.stringify({ filters, sort })}`
      return dedupe(key, async () => {
        const rows = applyMockSort(applyMockFilters(portfolio.rows, filters), sort)
        const items = await apiResolve(createTrademarkList(rows), {
          signal,
          path: '/brands',
          method: 'GET',
        })
        return { items, total: items.length, limit: items.length, offset: 0 }
      })
    }
    const page: Paged<BrandOut> = await http.get('/brands', {
      params: toParams(filters, sort),
      signal,
    })
    return { ...page, items: (page.items ?? []).map(trademarkFromApi) }
  },

  async summary({ signal }: ServiceOptions = {}): Promise<TrademarkSummary> {
    if (!LIVE()) {
      return apiResolve({ ...EMPTY_SUMMARY, ...portfolio.summary } as TrademarkSummary, {
        signal,
        path: '/brands/summary',
      })
    }
    return summaryFromApi(await http.get('/brands/summary', { signal }))
  },

  async get(id: string | number, { signal }: ServiceOptions = {}): Promise<Trademark | null> {
    if (!LIVE()) {
      const row = portfolio.rows.find((r) => String(r.id) === String(id))
      return apiResolve(row ? createTrademark(row) : null, {
        signal,
        path: `/brands/${id}`,
        delayMs: 80,
      })
    }
    const data = await http.get(`/brands/${id}`, { signal })
    return data ? trademarkFromApi(data) : null
  },

  async create(payload: Filters, { signal }: ServiceOptions = {}): Promise<Trademark> {
    if (!LIVE()) {
      const echoed = await apiEcho(payload, { signal, path: '/brands', method: 'POST' })
      return createTrademark(echoed)
    }
    return trademarkFromApi(await http.post('/brands', payload, { signal }))
  },

  async update(
    id: string | number,
    payload: Filters,
    { signal }: ServiceOptions = {}
  ): Promise<Trademark> {
    if (!LIVE()) {
      const echoed = await apiEcho(payload, { id, signal, path: `/brands/${id}`, method: 'PATCH' })
      return createTrademark(echoed)
    }
    return trademarkFromApi(await http.patch(`/brands/${id}`, payload, { signal }))
  },

  async remove(id: string | number, { signal }: ServiceOptions = {}): Promise<boolean> {
    if (!LIVE()) {
      return apiResolve(true, { signal, path: `/brands/${id}`, delayMs: 80 })
    }
    await http.delete(`/brands/${id}`, { signal })
    return true
  },

  /* ---------------------------------------------------------------- documents
   *
   * Files are streamed through the API rather than served from a path, because
   * a static URL would hand out bytes with no tenancy check (D-019). That is
   * why downloading needs a fetch-and-blob rather than a plain link.
   */

  async documents(brandId: string | number, { signal }: ServiceOptions = {}): Promise<BrandDoc[]> {
    if (!LIVE()) return apiResolve([], { signal, path: `/brands/${brandId}/documents` })
    return http.get(`/brands/${brandId}/documents`, { signal })
  },

  async uploadDocument(
    brandId: string | number,
    file: File,
    kind: DocumentKind = 'other',
    { signal }: ServiceOptions = {}
  ): Promise<BrandDoc> {
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    /* No Content-Type header on purpose: the browser has to set it *with* the
     * multipart boundary, and naming it here would produce a body the server
     * cannot parse. */
    return http.request('POST', `/brands/${brandId}/documents`, { body: form, signal })
  },

  async deleteDocument(
    brandId: string | number,
    documentId: number,
    { signal }: ServiceOptions = {}
  ): Promise<void> {
    await http.delete(`/brands/${brandId}/documents/${documentId}`, { signal })
  },

  /** Fetch a document's bytes as an object URL the browser can open or save. */
  async documentUrl(brandId: string | number, documentId: number): Promise<string> {
    const blob: Blob = await http.get(`/brands/${brandId}/documents/${documentId}/content`, {
      responseType: 'blob',
    })
    return URL.createObjectURL(blob)
  },

  /**
   * The mark's logo as something an `<img>` can load.
   *
   * Fetched rather than linked: the endpoint is authenticated, so a plain
   * `src="/api/brands/1/logo"` sends no token and renders a broken image.
   * Returns null when the mark has no logo, or when the bytes have gone
   * missing — a broken image icon on a lawyer's dossier reads as a broken
   * page, and an absent logo is not an error worth a toast.
   */
  async logoUrl(brandId: string | number): Promise<string | null> {
    if (!LIVE()) return null
    try {
      const blob: Blob = await http.get(`/brands/${brandId}/logo`, { responseType: 'blob' })
      return URL.createObjectURL(blob)
    } catch {
      return null
    }
  },

  /** The feeds a mark can be watched on, for the scope picker (D-005/D-014). */
  async feeds({ signal }: ServiceOptions = {}): Promise<
    { id: number; code: string; name: string; kind: string; available: boolean }[]
  > {
    if (!LIVE()) return apiResolve([], { signal, path: '/catalogues/feeds' })
    const feeds = await http.get('/catalogues/feeds', { signal })
    for (const feed of feeds) FEED_CODES[feed.id] = feed.code
    return feeds
  },

  /** NICE classes for the class picker — 45 of them, bilingual. */
  async niceClasses({ signal }: ServiceOptions = {}): Promise<
    { class_number: number; kind: string; titles: Record<string, string> }[]
  > {
    if (!LIVE()) return apiResolve([], { signal, path: '/catalogues/nice' })
    return http.get('/catalogues/nice', { signal })
  },
}
